/**
 * Sprint 23.6 v2 — AgentLiveActivity (Hero section 3, conditional).
 *
 * "Watch her work" (Q3 second section). A transparent section (hairline-ruled
 * like the rest of the hero) with a title row — "Live" on the left, a status
 * mini (dot + verb/duration) on the right — then an inner card holding the
 * current-task title and a timeline-bordered action stream.
 *
 * The stream's left border + indented timestamp columns ARE the visual (per the
 * contract): border-left 2px + padding-left 10px, each row = a 40px-min
 * timestamp column then the action text.
 *
 * Polling rides the shared `useWorkforceStatus` (30s, tab-blur paused) +
 * `useActivitiesByActor`. Hides entirely when not working and no recent
 * activity (Q21).
 */

import { useMemo, type CSSProperties } from "react";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { useActivitiesByActor } from "../../hooks/use-activities";
import { useThemedLabels } from "../../hooks/use-themed-labels";

export interface AgentLiveActivityProps {
  agentId: string;
  agentName: string;
}

export function AgentLiveActivity({ agentId }: AgentLiveActivityProps) {
  const labels = useThemedLabels();
  const status = useWorkforceStatus([agentId]);
  const myStatus = status.data?.[agentId] ?? null;
  const recent = useActivitiesByActor("agent", agentId, 4);

  const isWorking = myStatus?.status === "working";
  const verb = myStatus?.verb ?? null;
  // Bug 1 (Sprint 27.0): events whose subject is archived must not surface here.
  const recentItems = (recent.data ?? []).filter((a) => !a.archived_at);
  const showSection = isWorking || recentItems.length > 0;

  const renderTime = useMemo(() => (iso: string) => streamTime(iso, Date.now()), []);

  if (!showSection) return null;

  const dur = myStatus?.duration_seconds != null ? formatDuration(myStatus.duration_seconds) : null;
  const streamRows = recentItems.slice(0, isWorking && verb ? 4 : 4);

  return (
    <section style={wrap}>
      <div style={titleRow}>
        <h3 style={titleText}>Live</h3>
        <span style={statusMini}>
          <span style={{ ...statusDot, background: isWorking ? "var(--color-text-success)" : "var(--color-text-tertiary)" }} />
          {isWorking
            ? `${labels.statusWorking}${dur ? ` · ${dur}` : ""}`
            : `Last active${dur ? ` · ${dur} ago` : ""}`}
        </span>
      </div>
      <div style={card}>
        {isWorking && verb && <div style={taskTitle}>{verb}</div>}
        {streamRows.length > 0 && (
          <div style={stream}>
            {streamRows.map((a) => (
              <div key={a.id} style={streamItem}>
                <span style={streamTimeCol}>{renderTime(a.occurred_at)}</span>
                <span style={streamAction}>{a.subject}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function streamTime(iso: string, nowMs: number): string {
  const t = Date.parse(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (sec < 60) return "just now";
  return `${formatDuration(sec)} ago`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const wrap: CSSProperties = {
  padding: "14px 0",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
};

const titleRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: 6,
  gap: 8,
};

const titleText: CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const statusMini: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const statusDot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
};

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 14px",
};

const taskTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  marginBottom: 8,
};

const stream: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  lineHeight: 1.7,
  borderLeft: "2px solid var(--color-border-secondary)",
  paddingLeft: 10,
};

const streamItem: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
};

const streamTimeCol: CSSProperties = {
  color: "var(--color-text-tertiary)",
  fontSize: 11,
  minWidth: 40,
  flexShrink: 0,
};

const streamAction: CSSProperties = {
  minWidth: 0,
};
